import { useState } from "react";
import {
  Box,
  Collapse,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  formatRecipeQuantityInput,
  normalizeRecipeQuantityInput,
} from "../utils/recipeForm.utils";

export default function RecipeComponentRow({
  component,
  error,
  disabled,
  onChange,
  onRemove,
}) {
  const [notesOpen, setNotesOpen] = useState(Boolean(component.notes));

  return (
    <Box sx={{ py: 0.7, borderBottom: "1px solid rgba(64,64,94,.12)" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr) 34px 34px",
            sm: "minmax(120px, 1fr) 135px 38px 34px",
          },
          gap: 0.7,
          alignItems: "center",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            noWrap
            title={component.name}
            sx={{ fontSize: 12.5, fontWeight: 800, color: "primary.900" }}
          >
            {component.name}
          </Typography>
          <Typography sx={{ fontSize: 10.5, color: "neutral.600" }}>
            {component.kind === "ingredient" ? "Insumo" : "Elaborado"}
          </Typography>
        </Box>

        <TextField
          value={component.quantity}
          onChange={(event) =>
            onChange({ quantity: normalizeRecipeQuantityInput(event.target.value) })
          }
          onBlur={() =>
            onChange({ quantity: formatRecipeQuantityInput(component.quantity) })
          }
          disabled={disabled}
          error={Boolean(error)}
          placeholder="Cantidad"
          size="small"
          inputProps={{
            inputMode: "decimal",
            maxLength: 14,
            "aria-label": `Cantidad de ${component.name}`,
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Typography sx={{ fontSize: 11, fontWeight: 800, color: "neutral.700" }}>
                  {component.unitLabel}
                </Typography>
              </InputAdornment>
            ),
          }}
          sx={{
            gridColumn: { xs: "1 / -1", sm: "auto" },
            gridRow: { xs: 2, sm: "auto" },
            "& .MuiOutlinedInput-root": {
              height: 34,
              borderRadius: "10px",
              bgcolor: "white",
              fontSize: 12,
            },
            "& .MuiInputAdornment-root": { fontSize: 11, fontWeight: 700 },
          }}
        />

        <IconButton
          type="button"
          size="small"
          disabled={disabled}
          onClick={() => setNotesOpen((open) => !open)}
          aria-label={`${notesOpen ? "Ocultar" : "Mostrar"} notas de ${component.name}`}
          sx={{
            gridColumn: { xs: 2, sm: "auto" },
            gridRow: { xs: 1, sm: "auto" },
            transform: notesOpen ? "rotate(180deg)" : "none",
            transition: "transform .15s",
          }}
        >
          <ExpandMoreIcon fontSize="small" />
        </IconButton>
        <IconButton
          type="button"
          size="small"
          disabled={disabled}
          onClick={onRemove}
          aria-label={`Quitar ${component.name}`}
          sx={{
            gridColumn: { xs: 3, sm: "auto" },
            gridRow: { xs: 1, sm: "auto" },
            color: "error.main",
          }}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Box>

      {component.isAvailable === false ? (
        <Typography sx={{ mt: 0.3, fontSize: 10.5, fontWeight: 800, color: "error.main" }}>
          Inactivo — reemplazalo o quitálo para guardar
        </Typography>
      ) : null}
      {error ? (
        <Typography sx={{ mt: 0.3, fontSize: 10.5, color: "error.main" }}>
          {error}
        </Typography>
      ) : null}

      <Collapse in={notesOpen}>
        <TextField
          value={component.notes}
          onChange={(event) => onChange({ notes: event.target.value })}
          disabled={disabled}
          fullWidth
          size="small"
          placeholder="Nota opcional para este componente"
          inputProps={{ maxLength: 5000 }}
          sx={{
            mt: 0.65,
            "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: "white", fontSize: 12 },
          }}
        />
      </Collapse>
    </Box>
  );
}
